import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { checkoutSwitchedOff, hotelCheckoutOff } from '@/features/master/masterSlice';
import { padZeros } from '@/app/api/common';
import { getUnixEUTime, copyProjectRoom, getProjectRoom, HotelInfo, HotelInfoPublic, HotelLimits, HotelRoom, LanguageSelectorConfig, FirmwareDescriptor } from '@/features/projects/projectsSlice';
import { canRunDeviceCommands, ChannelListDescriptor, ChannelListManager, copyChannelList, getUrl, getField, languageSelectorAclKey, setField, UrlInfo } from '@/features/channels/channelsSlice';
import { isRoomFirmwareAvailable } from '@/features/firmware/firmwaresSlice';
import { canOverrideScreentime, resolveScreenTime } from '@/features/screentime/screenTimeApi';
import { canOverrideStatistics } from '@/features/appusage/appUsageApi';

import { useSpeedTest } from '@/app/websocket/hooks';
import BreadCrumbs from '@/components/Common/BreadCrumbs';
import Button from '@/components/Common/Button';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
//import Field from '@/components/Common/Field';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import Input, { TextArea } from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import Tabs from '@/components/Common/Tabs';
import UrlsEditor from '@/components/Common/UrlsEditor';
import FirmwaresEditor from '@/components/Common/FirmwaresEditor';

import { CatHeader } from './Project';
import EnabledApps  from './EnabledApps';
import LanguageSelectorSettings from './LanguageSelectorSettings';
import ProjectLists from './ProjectLists';
import ProjectTheme from './ProjectTheme';
import RoomCheckoutPanel from './RoomCheckoutPanel';
import RoomSpeedTest from './RoomSpeedTest';
import RoomStatistics from './RoomStatistics';
import ScreenTimeSettings from './ScreenTimeSettings';
import WelcomeWizard from './WelcomeWizard';

import { getRoomUrl } from './ProjectRooms';
import { isRoomNumberWritten, isRoomRename, prepareRoomForSave, validateRoomNumberChange } from './roomNumberValidation';


export interface ProjectRoomProps {
  project:				HotelInfo;
  setProject:			(val: HotelInfo) => any;
  pub:					HotelInfoPublic | null;
  room_no:				string;
  limits:				HotelLimits | null;
  setLimits:			(lim: HotelLimits) => any;
  setRoom:				(room: HotelRoom) => Promise<boolean>;
  triedSave:			boolean;
  setUrlInvalid:		(val: boolean) => any;
}

const ProjectRoom = (props: ProjectRoomProps) => {
  const { t } = useTranslation();
  
  const navigate = useNavigate();
  const { sub_tab } = useParams();
  
  const role = React.useContext(RoleContext);
  const logins = useAppSelector((state) => state.logins);
  const master = useAppSelector((state) => state.master);
  const checkoutOff = checkoutSwitchedOff(master);
  // #91 §0.8 — the hotel's own Checkout value wins over System settings.
  const hotelOff = hotelCheckoutOff(props.limits ? props.limits.checkout_enabled : undefined, master);
  const isProjectMember = React.useMemo(() => {
    const username = logins.login?.username;
    if (!username || !props.project) return false;
    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === props.project.id) return true;

    if (props.project.owner === username) return true;
    if (!props.limits) return false;
    const execs = props.limits.executives;
    return execs?.partner?.includes(username) ||
           execs?.chain_manager?.includes(username) ||
           execs?.manager?.includes(username);
  }, [logins.login, props.project, props.limits]);
  const canSaveRoom = role.admin
    || role?.privilages?.includes("EditProjectSettings")
    || role?.privilages?.includes("SetPartner")
    || role?.privilages?.includes("SetChainmanager")
    || role?.privilages?.includes("SetManager")
    || role?.privilages?.includes("SetChannelList")
    || isProjectMember;
  const speedTest = useSpeedTest();

  const [room, setRoom] = React.useState(null as HotelRoom | null);
  const [changes, setChanges] = React.useState(false);
  const [roomTriedSave, setRoomTriedSave] = React.useState(false);
  // Set once RoomStatistics reports BOTH its screen-time and app-usage
  // panels unavailable (503 recording off server-wide, or a flat 403 on
  // both reads) — meaning "hide the whole tab".
  const [statisticsHidden, setStatisticsHidden] = React.useState(false);

  const handleStatisticsAvailability = React.useCallback((available: boolean) => {
    setStatisticsHidden(!available);
  }, []);

  const changesRef = React.useRef(false);
  React.useEffect(() => { changesRef.current = changes; }, [changes]);
  const prevRoomNoRef = React.useRef(props.room_no);
  
  const globalLists = useAppSelector((state) => state.channelLists);
  const hotelChannelLists = useAppSelector((state) => state.hotelChannelLists);
  const projectFirmware = useAppSelector((state) => state.firmwares.projectFirmware);
  const languages = useAppSelector((state) => state.languages);
  const firmwareList = useAppSelector((state) => state.firmwares.firmwares);
  const trackFormats = useAppSelector((state) => state.trackFormats);
  const [combinedLists, setCombinedLists] = React.useState({lists: null} as ChannelListManager);
  
  const active =	sub_tab
  					?	sub_tab
  					:	"";
  
  React.useEffect(() => {
    // Track which room is currently be'ing viewed for WS notification filtering
    store.setCurrentRoom(props.room_no);

    // Fetch latest room data from API
    if (props.project?.id && props.room_no && props.room_no !== '__new__') {
      store.getRoom(props.project.id, props.room_no);
    }

    return () => {
      store.setCurrentRoom(null);
    };
  }, [props.project?.id, props.room_no]);

  React.useEffect(() => {
    if (!globalLists.lists) {
      store.getChannelLists();
    }

    if (props.project) {
      store.getChannelLists(props.project.id, false);
    }
    if (role.admin || role?.privilages?.includes("ViewTrackFormats")) {
      store.getTrackFormats();
    }
  }, []);
  
  React.useEffect(() => {
    const combined: ChannelListManager = {
      lists: [],
    };
    
    //console.log("ProjectRoom/useEffect/combined=", combined, "/globalLists=", globalLists);
    
    if (!combined.lists)
      return;

    for (const list of hotelChannelLists.lists) {
      combined.lists.push(copyChannelList(list));
    }

    if (globalLists.lists) {
      for (const list of globalLists.lists) {
        combined.lists.push(copyChannelList(list));
      }
    }
    
    setCombinedLists(combined);
    
    //console.log("ProjectRoom/useEffect/combined=", combined);
  }, [globalLists.lists, hotelChannelLists.lists, role.admin]);
  
  const updateRoom = React.useCallback((rume: HotelRoom) => {
    setChanges(true);
    setRoom(rume);
  }, [setRoom]);

  // The number the room is STORED under, taken from the URL segment the page
  // resolved it from — never from room.room_no, which is the number the
  // operator is currently TYPING. Every read-only lookup that addresses the
  // server (device commands, statistics, firmware availability) has to use
  // this one, or a rename typed but not yet saved would point them at a room
  // that does not exist. After a committed rename the segment is the new
  // number, so it stays correct on both sides of the save.
  const storedRoomNo =	props.room_no &&
						props.room_no != '__new__'
						?	props.room_no
						:	room
							?	room.room_no
							:	'';

  const [skywayPending, setSkywayPending] = React.useState<"reboot" | "standby" | null>(null);
  const runSkywayCommand = React.useCallback(async (action: "reboot" | "standby") => {
    if (!props.project?.id || !storedRoomNo || skywayPending) return;
    setSkywayPending(action);
    try {
      await store.skywayCommand(props.project.id, storedRoomNo, action);
    } finally {
      setSkywayPending(null);
    }
  }, [props.project?.id, storedRoomNo, skywayPending]);

  // Firmware validation for room level (must be before saveRoom)
  const roomFirmwareInvalid = React.useMemo(() => {
    const firmware = room?.overrides?.firmware;
    if (!firmware || firmware.length === 0) return false;

    for (const fw of firmware) {
      // If platform is selected, model must be selected
      if (fw.platform && fw.platform !== '' && (!fw.model || fw.model === '')) {
        return true;
      }
      // If model is selected, version must be selected
      if (fw.model && fw.model !== '' && (!fw.version || fw.version === '')) {
        return true;
      }
    }
    return false;
  }, [room]);

  // The §6 room-number rules, checked only when this save actually writes a
  // number (create or rename) — an ordinary edit round-trips the stored one
  // and the server does not re-check it, so a room already stored under a
  // number today's rules reject stays editable and renameable away.
  // Must be before saveRoom, like roomFirmwareInvalid above.
  const roomNoIssue = React.useMemo(() => validateRoomNumberChange(room), [room]);
  const roomNoInvalid = roomNoIssue != '';
  // Pending rename: the operator has typed a number this save would write,
  // but has not committed it yet.
  const roomNoRenamed = React.useMemo(() => isRoomNumberWritten(room), [room]);

  const saveRoom  = React.useCallback(async (rume: HotelRoom) => {
    // Check firmware validation before saving
    if (	roomFirmwareInvalid ||
			roomNoInvalid
	   ) {
      setRoomTriedSave(true);
      return;
    }
    // Copy first: the body is a full-object replace and the state object the
    // form is still rendering must not be mutated. The number is TRIMMED,
    // never whitespace-stripped — the old global strip turned an ordinary
    // save of "Room 101" into an unrequested rename to "Room101" (§10).
    const toSave = prepareRoomForSave(rume);
    const renamed = isRoomRename(toSave);

    const success = await props.setRoom(toSave);

    // The page resolves its room from the URL segment, so after a committed
    // rename that segment names nothing — walk the address bar, breadcrumb
    // and WS room filter over to the new number. A create still navigates
    // the way it always did.
    if (toSave.is_new) {
      navigate(getRoomUrl(toSave.id, toSave.room_no));
    } else
    if (	success &&
			renamed
	   ) {
      navigate(getRoomUrl(toSave.id, toSave.room_no));

      // The rooms list is keyed by room number — both the grid's row id and
      // the WS patch reducer — so a rename must be refetched, not patched in
      // place. props.setRoom already asks for one; this second, delayed pass
      // covers the transient empty/404 the project can answer with while it
      // is still mid-save (§9).
      setTimeout(() => store.getProjects(toSave.id), 500);
    }
    setRoomTriedSave(false);

    // Clearing the latch here is load-bearing for the language selector
    // override (§4.7): the refetch that follows a successful save replaces
    // `room` with the server-minted keys, but the effect that does that
    // bails out early while `changes` is still true. Leaving it set would
    // make every subsequent Save re-post the override's key:"" rows and mint
    // fresh uuids each time — rotating every guest's stored key on every
    // unrelated room save. Regression check: add a pair, Save, Save again
    // untouched — the second save must report "no updates".
    if (success)
      setChanges(false);
  }, [props.setRoom, roomFirmwareInvalid, roomNoInvalid]);

  // New overrides are seeded with the project's pairs INCLUDING their keys,
  // so switching a room to an override that starts as a copy does not
  // re-prompt guests — their stored keys still match. Only rows the operator
  // adds afterwards start with key:"" and get minted server-side.
  const startLanguageSelectorOverride = React.useCallback(() => {
    if (!room)
      return;

    const projectConfig = props.limits?.language_selector;

    const rume = copyProjectRoom(room);
    rume.language_selector = {
      enabled:	projectConfig ? projectConfig.enabled : false,
      pairs:		projectConfig ? projectConfig.pairs.map((p) => ({ ...p })) : [],
    };

    updateRoom(rume);
  }, [room, props.limits, updateRoom]);

  const removeLanguageSelectorOverride = React.useCallback(() => {
    if (!room)
      return;

    const rume = copyProjectRoom(room);
    rume.language_selector = undefined;

    updateRoom(rume);
  }, [room, updateRoom]);

  const updateLanguageSelectorOverride = React.useCallback((cfg: LanguageSelectorConfig) => {
    if (!room)
      return;

    const rume = copyProjectRoom(room);
    rume.language_selector = cfg;

    updateRoom(rume);
  }, [room, updateRoom]);
  
  const updateRoomFields = React.useCallback((urls: UrlInfo[]) => {
    if (!room)
      return;
    
    let rume = copyProjectRoom(room);
    rume.fields = urls;
    
    updateRoom(rume);
  }, [room, updateRoom]);
  
  const updateRoomNo = React.useCallback((room_no: string) => {
    if (!room)
      return;

    let rume = copyProjectRoom(room);
    rume.room_no = room_no;

    updateRoom(rume);
  }, [room, updateRoom]);
  
  const updateDuid = React.useCallback((val: string) => {
    if (!room)
      return;
    
    let rume = copyProjectRoom(room);
    rume.duid = val;
    
    updateRoom(rume);
  }, [room, updateRoom]);
  
  // The room's own free-text note. Stored as an ordinary `notes` row in
  // room.fields — the same shape the project's Notes uses in project.urls —
  // so nothing new appears on the wire and a room save carries it like any
  // other field. room.fields, never room.info.fields: the latter is what the
  // TV reported and a save must never write into it.
  const updateNotes = React.useCallback((val: string) => {
    if (!room)
      return;
    
    let rume = copyProjectRoom(room);
    rume.fields = setField(rume.fields || [], "notes", val);
    
    updateRoom(rume);
  }, [room, updateRoom]);
  
  const updateChannelLists = React.useCallback((descs: ChannelListDescriptor[]) => {
    if (!room)
      return;
    
    let rume = copyProjectRoom(room);
    rume.channel_lists = descs;
    
    updateRoom(rume);
  }, [room, updateRoom]);
  
  // Check if save should be blocked due to validation errors
  const cannotSave = (roomTriedSave && roomFirmwareInvalid) || roomNoInvalid;

  // The rule is only worth complaining about once the operator has touched
  // the form (or already pressed Save) — an untouched, still-blank number on
  // a brand new room is not an error yet.
  const showRoomNoError =	roomNoInvalid &&
							(	changes ||
								props.triedSave ||
								roomTriedSave
							);
  const roomNoErrorMessage =	roomNoIssue
								?	t('projects:project:rooms:room_no:' + roomNoIssue)
								:	'';

  // Check if firmware is available for this room from the API response
  const roomFirmwareAvailable = React.useMemo(() => {
    if (!room || !storedRoomNo) return false;
    const available = isRoomFirmwareAvailable(projectFirmware, storedRoomNo);
    console.log("ProjectRoom/roomFirmwareAvailable/room_no=", storedRoomNo, "/projectFirmware=", projectFirmware, "/available=", available);
    return available;
  }, [projectFirmware, storedRoomNo]);

  const updateTracksOverride = React.useCallback((val: string) => {
    if (!room)
      return;
    
    let rume = copyProjectRoom(room);
    
    if (rume.overrides)
      rume.overrides.tracks = val;
    updateRoom(rume);
  }, [room, updateRoom]);

  // Handler for firmware changes
  const updateFirmware = React.useCallback((firmware: FirmwareDescriptor[]) => {
    if (!room)
      return;

    let rume = copyProjectRoom(room);

    if (!rume.overrides) {
      rume.overrides = {};
    }
    rume.overrides.firmware = firmware;
    updateRoom(rume);
  }, [room, updateRoom]);

  const languageOpts: FieldInfo[] = React.useMemo(() => {
    return [
      { value: "default", desc: "Project Default" },
      ...(languages?.languages?.map((lang: any) => ({
        value: lang.code,
        desc: lang.name.charAt(0).toUpperCase() + lang.name.slice(1),
      })) || []),
    ];
  }, [languages.languages]);

  const trackFormatOpts: FieldInfo[] = React.useMemo(() => {
    return [
      { value: "", desc: "Project Default" },
      ...(trackFormats?.track_formats?.map((tf: any) => ({
        value: tf.key,
        desc: tf.description,
      })) || []),
    ];
  }, [trackFormats.track_formats]);

  const updateLanguage = React.useCallback((val: string) => {
    if (!room)
      return;

    let rume = copyProjectRoom(room);
    const code = val === "default" ? "" : val;
    rume.fields = setField(rume.fields || [], "language", code);
    updateRoom(rume);
  }, [room, updateRoom]);

  // Get room's platform from info fields
  const roomPlatform = React.useMemo(() => {
    const roomFields = room?.info?.fields || [];
    return getField(roomFields, "last_platform") || '';
  }, [room]);

  // Only the dongle reports a second (ethernet) MAC; other devices have one NIC.
  const isDongle = React.useMemo(() => {
    const roomFields = room?.info?.fields || [];
    return (getField(roomFields, "last_variant") || '').toLowerCase() === "dongle25";
  }, [room]);

  const skywayEligible = React.useMemo(() => {
    const roomFields = room?.info?.fields || [];
    const platform = (getField(roomFields, "last_platform") || '').toLowerCase();
    return platform === "android" && isDongle;
  }, [room, isDongle]);

  // §2.5 — `mw_checkout` as the project read STORED it, which means the RAW
  // slice value and nothing downstream of it.
  //
  // Not `room`: that is the editable copy the page keeps. Not `props.pub`
  // either, even though it is "the project read" — Project.tsx hands down
  // copyProjectInfoPublic(projects.active), and copyProjectRoom deliberately
  // omits this read-only field so a room save can never send it back. Reading
  // the copy therefore always finds it missing, and the panel would tell every
  // admin their TV needs an app update.
  const storedRoom = useAppSelector((state) => {
    const active = state.projects.active;

    if (	!active ||
			active.id !== props.project.id ||
			!active.rooms ||
			!active.rooms.rooms
		 )
      return null;

    return getProjectRoom(active, storedRoomNo);
  });

  const storedMwCheckout = storedRoom ? (storedRoom.mw_checkout || null) : null;
  const storedRoomName = storedRoom ? (storedRoom.name || "") : "";
  const storedRoomNotes = storedRoom ? getField(storedRoom.fields || [], "notes") : "";


  React.useEffect(() => {
    const roomChanged = prevRoomNoRef.current !== props.room_no;
    prevRoomNoRef.current = props.room_no;
    if (!roomChanged && changesRef.current) {
      return;
    }

    setChanges(false);

    if (	!props.room_no ||
    		!props.pub ||
        	!props.pub.rooms ||
        	!props.pub.rooms.rooms
       ) {
      setRoom(null);
      return;
    }
    
    const rume: HotelRoom | null =
    	props.room_no == "__new__"
		?	{	id:					props.pub.id,
				old_id:				props.pub.id,
				old_room_no:		"",
				room_no:			"",
				name:				"",
				channels:			{
					epg_id:				"",
					old_epg_id:			"",
					name:				"",
					channels: 			[],
					hidden:				false,
					tags:				[],
					owner:				props.pub.owner,
				},
				channel_lists:		[],
				duid:				"",
				fields:				[],
				info: {
					fields:				[],
				},
				apps:				{
					apps:				[],
				},
				messages:			[],
				overrides:			{},
				is_new:				true,
			}
		:	getProjectRoom(props.pub, props.room_no);
    setRoom(rume);
    
    /*for (const room of props.pub.rooms.rooms) {
      if (room.room_no != props.room_no)
        continue;
      
      setRoom(room);
      return;
    }
    
    setRoom(null);*/
  }, [props.pub])
  
  const crumbs =	props.pub
  					?	[
     [	`/projects/${props.pub.id}`,
     	props.pub ? props.pub.name : '-',
     ],
     [	`/projects/${props.pub.id}/rooms`,
     	t('projects:project:rooms:caption'),
     ],
     [	'',
     	t("projects:project:rooms:room_no_n", {n: room ? room.room_no : '-'}),
     ],
  ]
  					:	[];
  
  const roomLists = room &&
  					room.channel_lists
  					?	room.channel_lists
  					:	[];
  
  const updateActive = (act: any) => {
    if (	!props.pub ||
        	!room
       )
      return;
    // The stored number, not the typed one: switching tabs mid-rename must
    // not walk the URL onto a room number that does not exist yet — the page
    // would re-resolve against it, find nothing and drop the unsaved edits.
    const safeRoomNo = encodeURIComponent(storedRoomNo);
    const pubId=encodeURIComponent(props.pub.id);
    const url = `/projects/${pubId}/rooms/${safeRoomNo}/${act}`;
    navigate(url);
    console.log("Project/Tabs/updateActive/act=" + act + "/url=" + url);
  };
  
  const loaded =	props.pub &&
     				room
     				?	true
     				:	false;
  
  const fields =	room &&
  					room.info &&
    				room.info.fields
    				?	room.info.fields
    				:	[];

  const tracksOverride =	room &&
  							room.overrides &&
  							room.overrides.tracks
  							?	room.overrides.tracks
  							:	"";
  const roomNotes =		room
							?	getField(room.fields || [], "notes")
							:	"";

  // Widens the Info-tab Save clause the way `roomNoRenamed` does: the note
  // lives on that tab, so anyone who may save this room has to get a Save
  // button once they have typed in it, admin or not — otherwise the text has
  // nowhere to go. Compared against the value the project read STORED, so
  // opening the room, or editing something else on another tab, does not
  // conjure a button on its own.
  const notesEdited = roomNotes != storedRoomNotes;

  const aclsUrls = props.limits?.acls ?? [];
  const hasSeeAll = role?.privilages?.includes("SeeAllProjects");
  const seeAllOnly = hasSeeAll && !role.admin && !role?.privilages?.includes("EditProjectSettings");
  const hasAccess = (aclKey: string) => role?.admin || getField(aclsUrls, aclKey) == "1";

  // Deliberately the ACL alone, with no privilege term on top (§4.4/§4.7) —
  // an ACL-only Manager who could not otherwise touch this room still gets
  // to edit its override and to save it.
  const canEditLanguageSelectorRoom = canSaveRoom || hasAccess(languageSelectorAclKey);

  // Screen time hangs off the `enable-screentime` Manager ACL alone — no
  // privilege on top of it. This still gates the room SETTINGS (quota,
  // window, message) and the erase/reset button only — never the tab itself.
  const screentimeEnabled = role.admin || canOverrideScreentime(aclsUrls);

  // The Statistics tab has its own `enable-statistics` ACL. While unset it
  // falls back to the screen-time grant above, so nothing changes for a
  // project until an admin explicitly toggles the new checkbox.
  const statisticsEnabled = role.admin || canOverrideStatistics(aclsUrls);

  // Device Commands (Restart / Standby) was admin-only, which hid the row from
  // a Manager completely. `enable-device-commands` is the per-project grant
  // that opens it, with no older ACL to fall back on: absent reads as off, so
  // a project that never ticked it keeps the admin-only row it has today.
  // Visibility only: `skywayEligible` still has to agree (no other device
  // reports the commands), and a SeeAllProjects-only Manager gets the row
  // read-only, the way the rest of this page treats that user.
  const deviceCommandsEnabled = canRunDeviceCommands(role.admin, aclsUrls);

  // The tab is additionally hidden once the server tells us BOTH its halves
  // are off (503 recording off server-wide, or a flat 403 on both reads).
  const statisticsAvailable =	statisticsHidden
									?	false
									:	statisticsEnabled;

  // If the tab is retired while we are standing on it, walk back to Info —
  // otherwise the click that opened it lands on a panel that renders nothing.
  // Gated on limits being in hand: until they are, the ACL reads as "off" and
  // a deep link to .../statistics would bounce before it ever rendered.
  React.useEffect(() => {
    if (	active == 'statistics' &&
			loaded &&
			props.limits &&
			!statisticsAvailable
		 )
      updateActive('');
  }, [active, loaded, props.limits, statisticsAvailable]);

  // "Is this room measured?" is decided from the settings we hold, after the
  // project -> room resolution — never from the response, which does not echo
  // the window (a window-only room reports quota_seconds: 0 too).
  const screenTimeResolved = React.useMemo(
    () => resolveScreenTime(room?.fields, props.limits?.urls),
    [room, props.limits]
  );

  const tabs: FieldInfo[] = [
    { value:	"",			desc:	"projects:project:rooms:info:caption" },
    { value:	"settings",	desc:	"projects:project:rooms:room_settings:caption" },
    ...(hasAccess("enable-theme") || hasSeeAll ? [{ value: "theme", desc: "projects:project:theme:caption" }] : []),
    ...(hasAccess("enable-apps") || hasSeeAll ? [{ value: "apps", desc: "projects:project:apps:caption" }] : []),
    ...(statisticsAvailable ? [{ value: "statistics", desc: "projects:project:statistics:caption" }] : []),
  ];
  console.log("ProjectRoom/loaded=" + loaded + "/props=", props, "/room=", room);
  const firmwareMsg= getField(fields, "firmware-update")
  const firmwareMessage = React.useMemo(() => {
    if (!firmwareMsg) return "";
    try {
      const parsed = JSON.parse(firmwareMsg);
      return parsed["firmware-message"] || "";
    } catch {
      return firmwareMsg;
    }
  }, [firmwareMsg]);
  console.log("firmwareMsg", firmwareMessage)

  // The device reports its last firmware upgrade through two info fields:
  // `last_firmware_message` is what the TV said about it ("upgrade accepted
  // for ….epk, awaiting reboot"), and `last_firmware_success` is the flag it
  // flips once the upgrade actually went through — "0" until then.
  const firmwareUpdateInfo = getField(fields, "last_firmware_message");
  const firmwareUpdateFlag = getField(fields, "last_firmware_success").trim();
  const firmwareUpdateSucceeded =	firmwareUpdateFlag != "" &&
									firmwareUpdateFlag != "0" &&
									firmwareUpdateFlag.toLowerCase() != "false";

  return (
  <FlexRow direction="column"
           align="start"
           className="pt-[32px] pl-[32px] pr[32px]">
    <BreadCrumbs pages={crumbs} />
    {props.project && props.project.stats &&
    <FlexRow className="gap-[5px]">
      <span className="material-icons !text-[--Primetime-main] text-[16px]">
        location_on
      </span>
      <span className="!text-black text-[14px]">
        { t("projects:total") }
      </span>
      <span className="text-[--Primetime-main] text-[14px]">
        { padZeros(props.project.stats.rooms.allocated, 3) }
      </span>
      <span className="!text-black text-[14px]">
        { t("projects:activated") }
      </span>
      <span className="text-[--Primetime-main] text-[14px]">
        { padZeros(props.project.stats.rooms.created, 3) }
      </span>
      <span className="!text-black text-[14px]">
        { t("projects:active") }
      </span>
      <span className="text-[--Primetime-main] text-[14px]">
        { padZeros(props.project.stats.rooms.connected, 3) }
      </span>
    </FlexRow>
    }
    {loaded && room && room.is_new &&
    <FlexRow direction="column"
		       align="start"
		       className="py-0 gap-[16px] max-w-[448px]">
		<CatHeader caption="projects:project:rooms:grid:room_no"/>
		<Input label={t('projects:project:rooms:room_no:caption')}
		       placeholder={t('projects:project:rooms:room_no:placeholder')}
		       isLoaded={loaded}
		       isInvalid={showRoomNoError}
		       errorMessage={roomNoErrorMessage}
		       value={room.room_no}
		       onValueChange={updateRoomNo} />
	</FlexRow>
    }
  {!room || !room.is_new &&
    <FlexRow direction="column"
             align="start"
             className="py-[16px]">
      <Tabs tabs={tabs}
            flex={1}
            active={active}
            setActive={updateActive} />
    {loaded &&
      <React.Fragment>
      {active == '' &&
        <FlexRow direction="column"
                 className="max-w-[500px]">
          <CatHeader caption="projects:project:rooms:info:basic"
	                 margin="mt-[8px]" />
	      {/* Editable, not read-only text: changing it IS the rename request
	          (§10). updateRoomNo sets room_no alone and leaves old_room_no —
	          the number the room is stored under — for the server to match
	          on. A pure rename needs no privilege beyond editing the room,
	          so the field follows the same gate as the Save button. */}
	      <Field label={t("projects:project:rooms:grid:room_no")}>
		    <Input label={t('projects:project:rooms:room_no:caption')}
		           value={room.room_no}
		           isReadOnly={seeAllOnly || !canSaveRoom}
		           isInvalid={showRoomNoError}
		           errorMessage={roomNoErrorMessage}
		           onValueChange={updateRoomNo} />
		  </Field>
	      <Field label={t("projects:project:rooms:grid:created_on")}>
		    { getUnixEUTime(room.created_on) }
		  </Field>
	    {(role.admin || hasSeeAll) &&
	      <Field label={t("projects:project:rooms:grid:duid")}>
		    <Input label={t('projects:project:rooms:grid:duid')}
				   value={room.duid}
				   isReadOnly={seeAllOnly}
				   onValueChange={updateDuid} />
		  </Field>
		}
	      <Field label={t("projects:project:rooms:grid:last_modified")}>
		    { getUnixEUTime(room.last_modified) }
		  </Field>
	      <Field label={t("projects:project:rooms:grid:last_auth")}>
		    { getUnixEUTime(room.last_auth) }
		  </Field>
	  {/* §3.10 — how this room's TV gets its updates. Next to "last seen"
	      because it keeps its last value while the set is off, so the two are
	      read together. Server-written: a room save never changes it, and it is
	      absent until the TV has been in touch once. */}
	  {getField(fields, "last_connection") != "" &&
	      <Field label={t("projects:project:rooms:grid:last_connection")}>
		    { getField(fields, "last_connection") == "websocket"
		      ?	t("projects:project:rooms:grid:connection_websocket")
		      :	getField(fields, "last_connection") == "http"
		      ?	t("projects:project:rooms:grid:connection_http")
		      :	getField(fields, "last_connection")
		    }
		  </Field>
	  }
	      <Field label={t("projects:project:rooms:grid:last_activated")}>
		    { getUnixEUTime(room.last_activated) }
		  </Field>
	      <Field label={t("projects:project:rooms:grid:platform")}>
		    { getField(fields, "last_platform") }
		  </Field>
	      <Field label={t("projects:project:rooms:grid:variant")}>
		    { getField(fields, "last_variant") }
		  </Field>
	      <Field label={t("projects:project:rooms:grid:last_ip")}>
		    { getField(fields, "last_ip") }
		  </Field>
	  { isDongle
	  	?	<React.Fragment>
	          <Field label={t("projects:project:rooms:grid:last_mac_wifi")}>
		        { getField(fields, "last_hw_addr") }
		      </Field>
	          <Field label={t("projects:project:rooms:grid:last_mac_sec")}>
		        { getField(fields, "last_hw_addr_sec") }
		      </Field>
	        </React.Fragment>
	  	:	<Field label={t("projects:project:rooms:grid:last_mac")}>
		        { getField(fields, "last_hw_addr") }
		    </Field>
	  }
	      <Field label={t("projects:project:rooms:grid:last_serial")}>
		    { getField(fields, "last_serial") }
		  </Field>
	      <Field label={t("projects:project:rooms:grid:client_version")}>
		    { getField(fields, "last_sw_version") }
		  </Field>
	      <Field label={t("projects:project:rooms:grid:firmware_version")}>
		    { getField(fields, "last_fw_version") }
		  </Field>
	      <Field label={t("projects:project:rooms:grid:device_model")}>
		    { getField(fields, "last_model") }
		  </Field>
      {
       firmwareMessage&&
	      <Field label={t("projects:project:rooms:grid:firmware_message")}>
		    { firmwareMessage }
		    </Field>

      }
      {firmwareUpdateInfo &&
	      <Field label={t("projects:project:rooms:grid:firmware_update_info")}>
		    <span className="r14 !text-black text-right break-words">
		      { firmwareUpdateInfo }
		    </span>
		  </Field>
      }
      {firmwareUpdateFlag &&
	      <Field label={t("projects:project:rooms:grid:firmware_update_status")}>
		    <span className={firmwareUpdateSucceeded ? "r14 text-[#4CAF50]" : "r14 text-[--Dark-Grey]"}>
		      { firmwareUpdateSucceeded
		        ?	t("projects:project:rooms:grid:firmware_update_success")
		        :	t("projects:project:rooms:grid:firmware_update_failed") }
		    </span>
		  </Field>
      }
      {/* The operator's own note about this room. Last of the Info-tab blocks
          that read the room itself, before the device panels below. Editable
          by anyone who may save this room, and READ-ONLY rather than hidden
          for a SeeAllProjects-only user — the note is worth reading even
          when it cannot be changed. */}
      <FlexRow direction="column"
               align="start"
               className="py-0 gap-[16px] w-full">
        <CatHeader caption="projects:project:rooms:info:notes"
                   margin="mt-[16px]" />
        <TextArea value={roomNotes}
                  isLoaded={loaded}
                  isReadOnly={seeAllOnly || !canSaveRoom}
                  aria-label={t("projects:project:rooms:info:notes")}
                  placeholder={t("projects:project:rooms:info:notes_desc")}
                  onValueChange={updateNotes} />
      </FlexRow>
      {deviceCommandsEnabled && skywayEligible &&
        <React.Fragment>
          <CatHeader caption="projects:project:rooms:info:device_commands"
                     margin="mt-[16px]" />
          <FlexRow className="gap-[12px] py-[8px]">
            <Button caption={t('projects:project:rooms:info:restart')}
                    icon="restart_alt"
                    isDisabled={seeAllOnly || skywayPending !== null}
                    onClick={() => runSkywayCommand("reboot")} />
            <Button caption={t('projects:project:rooms:info:standby')}
                    icon="power_settings_new"
                    layout="white"
                    isDisabled={seeAllOnly || skywayPending !== null}
                    onClick={() => runSkywayCommand("standby")} />
            {skywayPending !== null &&
              <CircularProgress aria-label="Sending device command" size="sm" />
            }
          </FlexRow>
        </React.Fragment>
      }
      {/* MW light Checkout (#91, §2.5) — admins only, right after Device
          commands. Deliberately NOT gated on skywayEligible: checkout works on
          any TV that reported it can clear, not just the Android dongle.
          Stays while the Checkout is switched off in System settings: it then
          shows the banner, a disabled Checkout and a usable Cancel (§3.2). */}
      {role.admin && !room.is_new &&
        <RoomCheckoutPanel projectId={props.project.id}
                           roomNo={storedRoomNo}
                           roomName={storedRoomName}
                           mwCheckout={storedMwCheckout}
                           duid={room.duid}
                           projectEnabled={(props.project.state || "enabled") !== "disabled"}
                           checkoutEnabled={!checkoutOff}
                           projectCheckoutEnabled={!hotelOff} />
      }
      {(role.admin || hasSeeAll) && room.duid && room.duid.trim() !== "" &&
        <RoomSpeedTest projectId={props.project.id}
                       roomNo={storedRoomNo}
                       canStart={role.admin} />
      }
      {/* {(role.admin || hasSeeAll) && room.duid && room.duid.trim() !== "" &&
        <React.Fragment>
          <CatHeader caption="projects:project:rooms:info:speed_test"
                     captionProps={{defaultValue: "Speed Test"}}
                     margin="mt-[16px]" />
          <FlexRow className="gap-[12px] py-[8px]">
            <Button caption="Run Speed Test"
                    icon="speed"
                    isDisabled={speedTest.status === 'pending'}
                    onClick={() => store.startSpeedTest(props.project.id, room.room_no)} />
            {speedTest.status === 'pending' &&
              speedTest.hotelId === props.project.id &&
              speedTest.roomNo === room.room_no &&
              <CircularProgress aria-label="Speed test running" size="sm" />
            }
            {speedTest.status === 'ack' &&
              speedTest.hotelId === props.project.id &&
              speedTest.roomNo === room.room_no &&
              <span className="r12 text-[#4CAF50]">Command sent to device</span>
            }
          </FlexRow>
          <Field label="Download Speed">
            { getField(fields, "speed_test_download") || '-' }
          </Field>
          <Field label="Upload Speed">
            { getField(fields, "speed_test_upload") || '-' }
          </Field>
          <Field label="Latency">
            { getField(fields, "speed_test_latency") || '-' }
          </Field>
          <Field label="Status">
            { getField(fields, "speed_test_status") || '-' }
          </Field>
          <Field label="Last Test">
            { getField(fields, "speed_test_timestamp") ? getUnixEUTime(parseInt(getField(fields, "speed_test_timestamp"))) : '-' }
          </Field>
        </React.Fragment>
      } */}
		  <FlexRow direction="column"
		           className="gap-[16px]">
		  {(role.admin || hasSeeAll || hasAccess("enable-channellist") || isProjectMember) && combinedLists.lists &&
	        <ProjectLists lists={roomLists}
                          setLists={(role.admin || role?.privilages?.includes("SetChannelList")) ? updateChannelLists : () => {}}
                          sources={combinedLists}
                          room={room} />
          }
          {(role.admin || hasSeeAll) &&
            <Dropdown label={t('projects:project:rooms:overrides:tracks')}
				      value={tracksOverride}
				      isLoaded={loaded}
				      isDisabled={seeAllOnly}
				      options={trackFormatOpts}
				      setValue={updateTracksOverride}>
		      <>
		      </>
	        </Dropdown>
	      }
         {hasAccess("enable-language") &&
	        <Dropdown label={t('projects:project:project_settings:categories:language')}
				      value={!getField(room?.fields || [], "language") ? 'default' : getField(room?.fields || [], "language")}
				      placeholder={t('projects:project:project_settings:categories:select_the_language')}
				      isLoaded={loaded}
				      isDisabled={seeAllOnly && !hasAccess("enable-language")}
				      options={languageOpts}
				      setValue={updateLanguage}>
		      <>
		      </>
	        </Dropdown>
        }
	      </FlexRow>
	    </FlexRow>
      }
      {active == 'settings' &&
        <React.Fragment>
          <WelcomeWizard project={props.project}
                         setProject={props.setProject}
                         limits={props.limits}
                         setLimits={props.setLimits}
                         room={room}
                         setRoom={updateRoom} />
        {/* Screen time only appears once the `enable-screentime` Manager ACL is
            on (or a legacy per-field key still stands in for it) — the ACL is
            the only gate, no privilege is consulted. */}
        {screentimeEnabled &&
          <ScreenTimeSettings limits={props.limits}
                              setLimits={props.setLimits}
                              room={room}
                              setRoom={updateRoom}
                              isLoaded={loaded}
                              isReadOnly={seeAllOnly || !canSaveRoom} />
        }
        {/* Same ACL-alone gate as the project button (§4.7). Three states:
            inherit (member absent, the default), override (the same editor
            bound to the room's own config) and remove-override (back to
            absent). The device wire never changes shape — the TV cannot
            tell, and must not care, whether pairs came from the project or
            the room. */}
        {hasAccess(languageSelectorAclKey) && room &&
          <FlexRow direction="column"
                   align="start"
                   className="py-0 gap-[16px] max-w-[560px]">
            <CatHeader caption="projects:project:language_selector:room:caption"
                       margin="mt-[8px]" />
          {room.language_selector === undefined || room.language_selector === null
            ?	<React.Fragment>
                  <span className="text-[--Dark-Grey] text-[12px] leading-[16px]">
                    { t('projects:project:language_selector:room:inherit_hint') }
                  </span>
                {!seeAllOnly && canEditLanguageSelectorRoom &&
                  <Button caption={t('projects:project:language_selector:room:set_override')}
                          icon="tune"
                          layout="white"
                          onClick={startLanguageSelectorOverride} />
                }
                </React.Fragment>
            :	<React.Fragment>
                  <span className="text-[--Dark-Grey] text-[12px] leading-[16px]">
                    { t('projects:project:language_selector:room:override_hint') }
                  </span>
                  <LanguageSelectorSettings config={room.language_selector}
                                            setConfig={updateLanguageSelectorOverride}
                                            lists={combinedLists}
                                            isLoaded={loaded}
                                            isReadOnly={seeAllOnly || !canEditLanguageSelectorRoom}
                                            triedSave={roomTriedSave}
                                            showTitle={false} />
                {!seeAllOnly && canEditLanguageSelectorRoom &&
                  <Button caption={t('projects:project:language_selector:room:remove_override')}
                          icon="layers_clear"
                          layout="white"
                          onClick={removeLanguageSelectorOverride} />
                }
                </React.Fragment>
          }
          </FlexRow>
        }
        {role.admin &&
          <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px] max-w-[448px]">
            <CatHeader caption="common:urls:caption"
	                   margin="mt-[8px]" />
            <UrlsEditor urls={room && room.fields ? room.fields : []}
				        setUrls={updateRoomFields}
				        triedSave={props.triedSave}
				        setUrlInvalid={props.setUrlInvalid} />
		  </FlexRow>
		}
		{(role.admin || hasSeeAll) &&
          <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px] max-w-[448px]">
            <CatHeader caption="projects:project:project_settings:firmware:caption"
	                   margin="mt-[8px]" />
            {
              (firmwareList.length !== 0 && roomFirmwareAvailable) ?
              <span className="text-[--Mid-Grey] text-[14px]">
                {t('projects:project:rooms:room_settings:firmware_version')}
              </span>
              :""
            }
            {roomFirmwareAvailable ? (
              <FirmwaresEditor
                firmwares={room?.overrides?.firmware || []}
                setFirmwares={updateFirmware}
                triedSave={roomTriedSave}
                defaultPlatform={roomPlatform}
              />
            ) : (
              <span className="text-[--Mid-Grey] text-[14px]">
                {t('projects:project:rooms:room_settings:no_firmware_available')}
              </span>
            )}
		  </FlexRow>
		}
	    </React.Fragment>
      }
      {active == 'theme' &&
        <ProjectTheme project={props.project}
                      setProject={props.setProject}
                      limits={props.limits}
                      setLimits={props.setLimits}
                      room={room}
                      setRoom={updateRoom} />
      }
      {	active == 'apps' &&
        <EnabledApps project={props.project}
                     setProject={props.setProject}
                     limits={props.limits}
                     setLimits={props.setLimits}
                     room={room}
                     setRoom={updateRoom} />
      }
      {	active == 'statistics' &&
        statisticsAvailable &&
        <RoomStatistics projectId={props.project.id}
                        roomNo={storedRoomNo}
                        configured={screenTimeResolved.configured}
                        canReset={screentimeEnabled}
                        onAvailability={handleStatisticsAvailability}
                        onOpenSettings={() => updateActive('settings')} />
      }
      </React.Fragment>
    }
    </FlexRow>
  }
    {/* The screen time tab is a read-only history panel — there is nothing on
        it to save, so the sticky save bar stays off it. The settings-tab
        clause also admits an ACL-only Manager with no other room privilege —
        without it, canEditLanguageSelectorRoom lets them edit the override
        above but leaves them with no Save button to persist it (the same
        two-independent-gates trap as Project.tsx's handleSave, §4.4). */}
    {/* `roomNoRenamed` widens the Info-tab clause: the room number lives on
        that tab and a pure rename needs no privilege beyond editing the room,
        so a non-admin who may save this room must get a Save button for it —
        otherwise they can type the new number and have nowhere to commit it.
        The button now stays visible while the number is invalid, disabled via
        cannotSave, so the message under the field explains the block.
        `notesEdited` widens it the same way, for the same reason: the room
        note is on the Info tab too, and a non-admin who may save this room
        has to be able to commit what they typed into it. */}
    {(canSaveRoom || (active == 'settings' && hasAccess(languageSelectorAclKey))) && active !== 'statistics' && (active !== '' || role.admin || room?.is_new || roomNoRenamed || notesEdited || role?.privilages?.includes("SetChannelList")) &&
    <FlexRow className="sticky bottom-0 p-[32px] bg-white z-10">
      <Button caption={t('projects:project:rooms:save')}
              icon="save"
              isDisabled={!changes || cannotSave}
              className="mt-[32px]"
              onClick={() => room && saveRoom(room)} />
    </FlexRow>
    }
  </FlexRow>
  );
};

export interface FieldProps {
  label?:				string;
  children?:			React.ReactNode;
}

const Field = (props: FieldProps) => {
  return (
  <FlexRow className="min-h-[24px] py-[15px] border-solid border-t-1 border-[--Mid-Grey]">
  {props.label &&
    <FlexRow className="font-medium r14 !text-black min-w-[200px]">
      { props.label }
    </FlexRow>
  }
  {props.children &&
    <FlexRow className="justify-end max-w-[200px]">
      { props.children }
    </FlexRow>
  }
  </FlexRow>
  );
};

export default ProjectRoom;



